import React from 'react'
import { Row, H2, Button, Subheading } from '@fs/zion-ui'
import { PersonFamily } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import {
  DISABLED_BACKGROUND_COLOR,
  DISABLED_FOREGROUND_COLOR,
  REVIEWED_COLOR,
  IN_PROGRESS_COLOR,
} from '../../shared/utils/panelConstants'

/**
 * FamilyMembersHeader
 *
 * Displays the title, contextual subheading, and (optionally) an Edit button for the
 * Family Members card. Icon background color reflects state:
 *  - Gray: Not yet reviewed (pending).
 *  - Green: Reviewed and not currently editing.
 *  - Yellow: In edit mode.
 *
 * When the card has been reviewed, an Edit button is shown to toggle edit mode.
 * The subheading is hidden once the section is reviewed (unless currently editing again).
 *
 * @component
 * @param {Object} props
 * @param {Function} props.setIsEditing Function to toggle edit state in the parent card.
 * @param {boolean} props.isEditing     Whether the parent card is in edit mode.
 * @param {boolean} [props.reviewed=false] Indicates if the section has been marked reviewed.
 * @returns {JSX.Element}
 */
function FamilyMembersHeader({ setIsEditing, isEditing, reviewed = false, disableEdit = false }) {
  const [t] = useTranslation()
  let iconColor = DISABLED_BACKGROUND_COLOR
  if (reviewed && !isEditing) {
    iconColor = REVIEWED_COLOR
  }
  if (isEditing) {
    iconColor = IN_PROGRESS_COLOR
  }
  return (
    <Row gutters="xxs" alignItemsY="middle">
      <PersonFamily
        size="sm"
        background={iconColor}
        color={!isEditing && !reviewed ? DISABLED_FOREGROUND_COLOR : undefined}
      />
      <H2 growX size="H5">
        {t('household-details-card.title', 'Household Details')}
        {(!reviewed || isEditing) && (
          <Subheading>{t('household-details-card.subheading', 'Household and Member Relationships')}</Subheading>
        )}
      </H2>

      {reviewed && !isEditing && (
        <Button
          noShrinkX
          onClick={() => setIsEditing(true)}
          linkName="household-details-edit-btn"
          disabled={disableEdit}
        >
          {t('household-details-card.edit.btn', 'Edit')}
        </Button>
      )}
    </Row>
  )
}

export default FamilyMembersHeader
